import React, { FC, useContext } from 'react'
import { useTranslation } from 'react-i18next'
import ConnStateUnknown from '@assets/images/common/conn-state-unknown.ico'
import { Button, FormGenerator } from '@microservices/wiskey-react-components'
import { Delete } from '@mui/icons-material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Grid,
  IconButton,
  Typography,
} from '@mui/material'

import { AddCommandsDialog } from '@pages/EntityCreateOrEdit'

import { PageContentLayout } from '@layouts/PageContentLayout'
import { CustomSvgIcon } from '@components/CustomSvgIcon'
import { ScriptValueDialog } from '@components/ScriptValueDialog'

import { BIND_TYPE } from '@constants'
import { FormCommand } from '@types'

import { TooltipStyled } from '@gantt/components/GanttCreateOrEdit/components/GanttConfiguration/components/StepWrap/TooltipStyled'

import { getObjectXTotalsRowScriptHint, getTotalsCommandScriptConfig } from '../../helpers'
import { useObjectXTotalsRow } from '../../hooks'
import { PageContext } from '../../TimeLineCreateOrEdit'

type Props = {
  index: number
}

export const ObjectXTotalsRow: FC<Props> = ({ index }) => {
  const { t } = useTranslation()

  const { handlers: contextHandlers, state: contextState, data: contextData } = useContext(PageContext)

  const { state, handlers, inputs } = useObjectXTotalsRow({ rowIndex: index })

  const { handleDeleteObjectXTotalRow } = contextHandlers
  const {
    watchObjectXObject,
    watchObjectYObject,
    watchVariables,
    watchMode,
    methods: {
      formState: { errors },
    },
  } = contextState
  const {objectXSelectedColumnsObjectFields} = contextData

  const {
    watchTitle,
    watchScript,
    watchBindType,
    showScriptDialog,
    watchCommands,
    showAddCommandsDialog,
  } = state

  const {
    handleChangeScript,
    handleChangeShowScriptDialog,
    handleChangeCommands,
    handleOpenAddCommandsDialog,
    handleCloseAddCommandsDialog,
  } = handlers

  const handleDelete = () => {
    handleDeleteObjectXTotalRow(index)
  }

  return (
    <PageContentLayout sx={{ p: 1 }}>
      <Accordion>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            my: 0,
            minHeight: '48px !important',
            '& .MuiAccordionSummary-content': {
              m: '0px !important',
            },
          }}
        >
          <Grid alignItems='center' display='flex' flex={1}>
            <Typography alignSelf={'center'}>
              {`${t('timeLinesCreateOrEdit.objectX.totals.row')} ${index + 1} ${watchTitle}`}
            </Typography>
            {Boolean(errors?.objectX?.totalsRows?.[index]) && (
              <TooltipStyled
                title={
                  <>
                    {t('ganttCreate.invalidFields')}
                    {Object.keys(errors?.objectX?.totalsRows?.[index] || {}).join(', ')}
                  </>
                }
              >
                <Typography color='error' variant='caption'>
                  <Box alignItems='center' display='flex' justifyContent='center'>
                    <CustomSvgIcon src={ConnStateUnknown} sx={{ width: 24, height: 26 }} />
                  </Box>
                </Typography>
              </TooltipStyled>
            )}
          </Grid>

          <IconButton
            title={t('timeLinesCreateOrEdit.objectX.totals.deleteRow')}
            onClick={handleDelete}
          >
            <Delete />
          </IconButton>
        </AccordionSummary>
        <AccordionDetails>
          <Grid px={1}>
            <FormGenerator inputs={inputs} />
            {showAddCommandsDialog && (
              <AddCommandsDialog
                commands={watchCommands as FormCommand[]}
                objectCode={watchObjectXObject?.id as string}
                commandPickerControllerProps={{
                  isTypeHidden: true,
                }}
                jsScriptConfig={getTotalsCommandScriptConfig({
                  mode: watchMode?.id,
                  columnsObjectFields: objectXSelectedColumnsObjectFields
                })}
                onChangeCommands={handleChangeCommands}
                onCloseDialog={handleCloseAddCommandsDialog}
              />
            )}

            {showScriptDialog && (
              <ScriptValueDialog
                isShow
                windowBoundaries
                isJsx={watchBindType?.id === BIND_TYPE.JSX}
                modalType={watchScript ? 'edit' : 'create'}
                value={watchScript as string}
                hint={getObjectXTotalsRowScriptHint({
                  objectX: watchObjectXObject,
                  objectY: watchObjectYObject,
                  variables: watchVariables,
                  commands: watchCommands,
                })}
                onClose={handleChangeShowScriptDialog}
                onSave={handleChangeScript}
              />
            )}
            <Grid container justifyContent={'flex-end'} width={'100%'}>
              <Button sx={{ width: 200 }} variant='outlined' onClick={handleOpenAddCommandsDialog}>
                {t('actionInputs.formCommands.label', {
                  mode: watchCommands?.length ? 'edit' : 'add',
                })}
              </Button>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
    </PageContentLayout>
  )
}
